import { useState } from 'react'

import AtSymbolIcon from '@heroicons/react/outline/AtSymbolIcon'
import ChatIcon from '@heroicons/react/outline/ChatIcon'
import CheckCircleIcon from '@heroicons/react/outline/CheckCircleIcon'
import CogIcon from '@heroicons/react/outline/CogIcon'
import UserIcon from '@heroicons/react/outline/UserIcon'
import ViewGridIcon from '@heroicons/react/outline/ViewGridIcon'
import clsx from 'clsx'

import { MemberEmailStatus } from '@tribeplatform/gql-client/types'
import {
  useAuthMember,
  useAuthToken,
  useCollections,
  useMemberPosts,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'

import { useAnalytics } from '../../hooks/useAnalytics'
import { VerifyModal } from '../Auth/VerifyModal'
import { PostModal } from '../Post/PostModal'
import { ProfileSettingsModal } from '../Settings/ProfileSettingsModal'
import { CreateSpaceModal } from '../Space/CreateSpaceModal'
import { UserImportModal } from '../UserImport'

export const MemberOnboarding = () => {
  const {
    data: { network },
  } = useAuthToken()
  const { data: member, isAdmin, isGuest } = useAuthMember()

  const { data: collections, isLoading: isCollectionLoading } = useCollections({
    fields: {
      space: {
        image: 'basic',
        banner: 'basic',
        authMemberProps: 'all',
      },
    },
    variables: {},
    useQueryOptions: {
      retry: 10,
      retryDelay: 1000,
      enabled: isAdmin,
    },
  })
  const { data: memberPosts, isLoading: isMemberPostLoading } = useMemberPosts({
    variables: { limit: 1, memberId: member?.id },
    useInfiniteQueryOptions: {
      refetchOnMount: 'always',
      enabled: isAdmin && !!member?.id,
    },
    fields: 'basic',
  })

  const [profileModal, setProfileModal] = useState(false)
  const [postModal, setPostModal] = useState(false)
  const [verifyModal, setVerifyModal] = useState(false)
  const [spaceModal, setSpaceModal] = useState(false)
  const [inviteModal, setInviteModal] = useState(false)

  const analytics = useAnalytics()

  if (isGuest || !isAdmin) return null
  if (isCollectionLoading || isMemberPostLoading) return null

  const steps = []

  if (isAdmin) {
    steps.push({
      order: 10,
      id: 'confirm-email',
      title: 'Confirm your email',
      description: 'Verify your identity.',
      icon: AtSymbolIcon,
      completed: member.emailStatus === MemberEmailStatus.VERIFIED,
      button: {
        cta: 'Confirm',
        completed: 'Confirmed',
        variant: 'primary',
        action: () => {
          setVerifyModal(true)
        },
      },
    })

    steps.push({
      order: 20,
      title: 'Upload your logo',
      id: 'upload-logo',
      description: 'Upload community logo and set access level.',
      icon: CogIcon,
      completed: network?.logoId || network?.faviconId,
      button: {
        cta: 'Setup',
        completed: 'Done',
        variant: 'outline',
        action: () => {
          document.location.href = '/admin/network/settings'
        },
      },
    })

    steps.push({
      order: 30,
      title: 'Define structure',
      id: 'create-space',
      description: 'Create collections and spaces',
      icon: ViewGridIcon,
      completed:
        collections
          ?.map(collection => collection?.spaces?.nodes?.length || 0)
          ?.reduce((count, n) => count + n) > 1,
      button: {
        cta: 'Create space',
        completed: 'Created',
        variant: 'outline',
        action: () => {
          setSpaceModal(true)
        },
      },
    })
  }

  steps.push({
    order: 40,
    title: 'Update your profile',
    id: 'update-profile',
    description: 'Add your photo and tagline.',
    icon: UserIcon,
    completed: member?.profilePictureId || member?.tagline,
    button: {
      cta: 'Update',
      completed: 'Updated',
      action: () => {
        setProfileModal(true)
      },
    },
  })

  steps.push({
    order: 50,
    title: 'Create a post',
    id: 'create-post',
    description: 'Engage with the community.',
    icon: ChatIcon,
    completed: memberPosts?.pages?.[0].totalCount > 0,
    button: {
      cta: 'Create post',
      completed: 'Done',
      action: () => {
        setPostModal(true)
      },
    },
  })

  // steps.push({
  //   order: 60,
  //   title: 'Invite members',
  //   id: 'invite-members',
  //   description: 'Invite your teammates and members.',
  //   icon: ChatIcon,
  //   completed: network?.membersCount > 1,
  //   button: {
  //     cta: 'Invite',
  //     completed: 'Done',
  //     action: () => {
  //       setInviteModal(true)
  //     },
  //   },
  // })

  if (steps?.filter(step => !step.completed)?.length === 0) return null

  steps.sort((a, b) => {
    if (a.completed && !b.completed) return 1
    if (b.completed && !a.completed) return -1
    return a.order > b.order ? 1 : -1
  })

  return (
    <>
      <Card className="overflow-hidden">
        <Card.Header
          title={isAdmin ? 'Setup your community' : 'Complete your profile'}
        />
        <div className="pt-3 pb-6 snap-x snap-mandatory relative w-full overflow-x-auto flex pr-6">
          {steps.map(step => {
            return (
              <div className="pl-6 snap-start flex" key={step.title}>
                <div
                  className={clsx(
                    'rounded-lg p-5 text-basicSurface-900 text-center flex-shrink-0 w-52 flex flex-col',
                    step.completed
                      ? 'opacity-60 bg-surface-100'
                      : 'bg-surface-100',
                  )}
                >
                  <div className="justify-center flex mb-3">
                    {step.completed ? (
                      <CheckCircleIcon className="w-8 h-8 text-actionPrimary-500" />
                    ) : (
                      <step.icon className="w-8 h-8" />
                    )}
                  </div>
                  <h3 className="text-base font-medium">{step.title}</h3>
                  <div className="text-basicSurface-400 flex-grow text-sm">
                    {step.description}
                  </div>
                  <div className="mt-6">
                    {step.completed ? (
                      <div className="text-actionPrimary-500 font-medium px-4 py-2 text-base">
                        {step.button.completed}
                      </div>
                    ) : (
                      <Button
                        size="lg"
                        variant={step?.button?.variant || 'outline'}
                        onClick={() => {
                          step?.button?.action?.()
                          analytics.track('Click Onboarding', {
                            title: step.title,
                            id: step.id,
                          })
                        }}
                      >
                        {step?.button?.cta}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </Card>
      {profileModal && (
        <ProfileSettingsModal
          member={member}
          open={profileModal}
          onClose={() => setProfileModal(false)}
        />
      )}
      {postModal && <PostModal onClose={() => setPostModal(false)} />}
      {verifyModal && (
        <VerifyModal open={verifyModal} onClose={() => setVerifyModal(false)} />
      )}
      {spaceModal && (
        <CreateSpaceModal
          open={spaceModal}
          onClose={() => setSpaceModal(false)}
        />
      )}
      {inviteModal && (
        <UserImportModal
          open={inviteModal}
          onClose={() => {
            setInviteModal(false)
          }}
        />
      )}
    </>
  )
}

MemberOnboarding.displayName = 'Member Onboarding'
